/**
 * features/settings/UsersSettingsTab.tsx — จัดการผู้ใช้ (admin only)
 */

import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { createUserSchema, USER_ROLE_LABELS, type CreateUserInput } from '@ft/core';
import { ApiError } from '~/lib/api';
import { useAuth } from '~/features/auth/auth-context';
import type { UserRecord } from '~/lib/types';
import { Button, Card, Field, Input, Select, TableSkeleton } from '~/ui/primitives';
import { ConfirmDialog, Dialog, useToast } from '~/ui/overlays';
import { useCreateUser, useDeleteUser, useUsers } from '~/features/users/hooks';

const EMPTY: CreateUserInput = { username: '', password: '', displayName: '', role: 'user' };

export function UsersSettingsTab() {
  const { user: currentUser } = useAuth();
  const { data: users, isLoading } = useUsers();
  const createUser = useCreateUser();
  const deleteUser = useDeleteUser();
  const toast = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CreateUserInput>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateUserInput, string>>>({});
  const [deleting, setDeleting] = useState<UserRecord | null>(null);

  function patchForm(patch: Partial<CreateUserInput>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  async function handleCreate() {
    const parsed = createUserSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof CreateUserInput, string>> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path[0] as keyof CreateUserInput] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    try {
      await createUser.mutateAsync(parsed.data);
      toast.success('เพิ่มผู้ใช้แล้ว');
      setFormOpen(false);
      setForm(EMPTY);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'เพิ่มผู้ใช้ไม่สำเร็จ');
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteUser.mutateAsync(deleting.id);
      toast.success('ลบผู้ใช้แล้ว');
      setDeleting(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button variant="primary" onClick={() => setFormOpen(true)}>
          <Plus size={16} />
          เพิ่มผู้ใช้
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <TableSkeleton columns={4} />
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>ชื่อผู้ใช้</th>
                  <th>ชื่อที่แสดง</th>
                  <th>บทบาท</th>
                  <th>สถานะ</th>
                  <th className="w-16" />
                </tr>
              </thead>
              <tbody>
                {users?.map((user) => (
                  <tr key={user.id}>
                    <td className="font-medium text-fg">{user.username}</td>
                    <td className="text-fg-muted">{user.displayName || '-'}</td>
                    <td className="text-fg-muted">{USER_ROLE_LABELS[user.role]}</td>
                    <td>
                      <span className={user.active ? 'text-success' : 'text-fg-subtle'}>
                        {user.active ? 'ใช้งานได้' : 'ปิดใช้งาน'}
                      </span>
                    </td>
                    <td>
                      {user.id !== currentUser?.id ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          aria-label="ลบ"
                          onClick={() => setDeleting(user)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      ) : (
                        <span className="text-xs text-fg-subtle">คุณ</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="เพิ่มผู้ใช้ใหม่"
        footer={
          <>
            <Button onClick={() => setFormOpen(false)}>ยกเลิก</Button>
            <Button variant="primary" onClick={() => void handleCreate()} loading={createUser.isPending}>
              เพิ่มผู้ใช้
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="ชื่อผู้ใช้" required error={errors.username} hint="a-z, 0-9, . _ - เท่านั้น">
            <Input value={form.username} onChange={(event) => patchForm({ username: event.target.value })} />
          </Field>
          <Field label="รหัสผ่าน" required error={errors.password} hint="อย่างน้อย 8 ตัวอักษร">
            <Input
              type="password"
              value={form.password}
              onChange={(event) => patchForm({ password: event.target.value })}
            />
          </Field>
          <Field label="ชื่อที่แสดง" error={errors.displayName}>
            <Input
              value={form.displayName}
              onChange={(event) => patchForm({ displayName: event.target.value })}
            />
          </Field>
          <Field label="บทบาท" error={errors.role}>
            <Select value={form.role} onChange={(event) => patchForm({ role: event.target.value as 'admin' | 'user' })}>
              <option value="user">ผู้ใช้งาน</option>
              <option value="admin">ผู้ดูแลระบบ</option>
            </Select>
          </Field>
        </div>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => void handleDelete()}
        title="ลบผู้ใช้"
        message={`ต้องการลบผู้ใช้ "${deleting?.username}" ใช่หรือไม่`}
        confirmLabel="ลบ"
        loading={deleteUser.isPending}
      />
    </div>
  );
}
